import { useEffect, useState } from 'react';

import http from '@/utils/http';
import { ROBLOX_TOP_CHARTS_DEFAULT_DATA_BY_DATE_PARAMS } from '../constants';
import { buildRobloxTopChartsComponentDefault } from '../utils/robloxTopChartsDefaultDate';

let cachedComponentDefault: Record<string, [string, string]> | null = null;
let pendingRequest: Promise<Record<string, [string, string]>> | null = null;

const getFallbackComponentDefault = (): Record<string, [string, string]> => {
  return buildRobloxTopChartsComponentDefault([]);
};

const fetchRobloxTopChartsDefaultDateOnce = async (): Promise<Record<string, [string, string]>> => {
  if (cachedComponentDefault) {
    return cachedComponentDefault;
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = http({
    url: '/api/v1/intelligence_pc/getDefaultDataByDate',
    method: 'post',
    data: ROBLOX_TOP_CHARTS_DEFAULT_DATA_BY_DATE_PARAMS,
    rawData: true,
    errorHandler: true,
  })
    .then((response: any) => {
      return buildRobloxTopChartsComponentDefault(response?.data || []);
    })
    .catch((error) => {
      console.debug('[RobloxTopCharts] fetch default date failed, use fallback', error);
      return getFallbackComponentDefault();
    })
    .then((nextComponentDefault) => {
      cachedComponentDefault = nextComponentDefault;
      return nextComponentDefault;
    })
    .finally(() => {
      pendingRequest = null;
    });

  return pendingRequest;
};

export default function useRobloxTopChartsDefaultDate(enabled = true) {
  const [loading, setLoading] = useState(() => enabled && !cachedComponentDefault);
  const [componentDefault, setComponentDefault] = useState<Record<string, [string, string]>>(
    () => cachedComponentDefault || getFallbackComponentDefault(),
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    if (cachedComponentDefault) {
      setComponentDefault(cachedComponentDefault);
      setLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setLoading(true);

    fetchRobloxTopChartsDefaultDateOnce()
      .then((nextComponentDefault) => {
        if (isCancelled) {
          return;
        }

        setComponentDefault(nextComponentDefault);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [enabled]);

  return {
    loading,
    componentDefault,
  };
}
